import type { BasicModel, DateTime, LooseObject } from '@rentguru/commons-utils';
import type { GraphQLClient } from 'graphql-request';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { graphqlClient } from './client';
import { MILLISECONDS_BEFORE_RETRYING_A_VERSION_CONFLICT, VERSION_CONFLICT_ATTEMPTS } from './constants';
import { isConditionalCheckFailedGraphQLError, toGraphQLApiError } from './errors';
import { excludeDeletedFromFilter, getFilters, getGraphQlMethodName } from './utils';

const waitBeforeVersionConflictRetry = (attempt: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, attempt * MILLISECONDS_BEFORE_RETRYING_A_VERSION_CONFLICT);
  });

// A backend stream can write the row between the fetch and the mutation (lease dates copied to units and operations).
const retryOnVersionConflict = async <ResultType>(
  writeWithLatestVersion: () => Promise<ResultType>,
  attempt = 1
): Promise<ResultType> => {
  try {
    return await writeWithLatestVersion();
  } catch (error) {
    if (!isConditionalCheckFailedGraphQLError(error) || attempt >= VERSION_CONFLICT_ATTEMPTS) {
      throw error;
    }
    await waitBeforeVersionConflictRetry(attempt);
    return await retryOnVersionConflict(writeWithLatestVersion, attempt + 1);
  }
};

export const createGraphqlOperations = (client: GraphQLClient) => {
  const fetchLatestEntityVersion = async <ResultType extends object>(
    entityId: string,
    getQuery: string
  ): Promise<BasicModel<ResultType>> => {
    const method = getGraphQlMethodName(getQuery, 'query');
    const variables = { id: entityId };
    try {
      const data = await client.request<{ [method: string]: ResultType | null }>(getQuery, variables);
      const entity = data[method] as BasicModel<ResultType> | null;
      if (isNil(entity)) {
        throw new Error(`${method}: entity ${entityId} no longer exists`);
      }

      return entity;
    } catch (error) {
      throw toGraphQLApiError(error, method);
    }
  };

  const query = async <ResultType, VariablesType extends object = object>(
    document: string,
    variables?: VariablesType,
    signal?: AbortSignal
  ): Promise<ResultType> => {
    const method = getGraphQlMethodName(document, 'query');
    try {
      const data = await client.request<{ [method: string]: ResultType }>({ document, signal, variables });
      return data[method];
    } catch (error) {
      throw toGraphQLApiError(error, method);
    }
  };

  const mutation = async <ResultType, VariablesType extends object = object>(
    mutationQuery: string,
    variables?: VariablesType
  ) => {
    const method = getGraphQlMethodName(mutationQuery, 'mutation');
    try {
      const data = await client.request<{ [method: string]: ResultType }>(mutationQuery, variables);
      return data[method] as ResultType;
    } catch (error) {
      throw toGraphQLApiError(error, method);
    }
  };

  const mutationWithFetchBefore = async <
    ResultType extends object,
    MutationVariablesType extends { input?: object; condition?: object | null } = object,
  >(
    entityId: string,
    getQuery: string,
    mutationQuery: string,
    mutationVariables: MutationVariablesType = {} as MutationVariablesType
  ) => {
    const mutateWithLatestVersion = async (): Promise<ResultType> => {
      const latestEntityVersion = await fetchLatestEntityVersion<ResultType>(entityId, getQuery);

      return await mutation<ResultType>(mutationQuery, {
        input: { ...mutationVariables.input, _version: latestEntityVersion._version },
        ...mutationVariables.condition,
      });
    };

    return await retryOnVersionConflict(mutateWithLatestVersion);
  };

  const deleteWithLatestVersion = async <
    ResultType extends object,
    DeleteVariablesType extends { input?: object; condition?: object | null } = object,
  >(
    entityId: string,
    getQuery: string,
    deleteQuery: string,
    deleteVariables: DeleteVariablesType
  ): Promise<ResultType> => {
    const latestEntityVersion = await fetchLatestEntityVersion<ResultType>(entityId, getQuery);

    return await mutation<ResultType>(deleteQuery, {
      input: { id: latestEntityVersion.id, _version: latestEntityVersion._version, ...deleteVariables.input },
      ...deleteVariables.condition,
    });
  };

  const deleteEntityWithFetchBefore = async <
    ResultType extends object,
    DeleteVariablesType extends { input?: object; condition?: object | null } = object,
  >(
    entity: ResultType,
    getQuery: string,
    deleteQuery: string,
    deleteVariables: DeleteVariablesType = {} as DeleteVariablesType
  ) => {
    const entityId = (entity as BasicModel<ResultType>).id;

    return await retryOnVersionConflict(() =>
      deleteWithLatestVersion<ResultType>(entityId, getQuery, deleteQuery, deleteVariables)
    );
  };

  const deleteAndHideEntityWithFetchBefore = async <
    ResultType extends object,
    DeleteVariablesType extends { input?: object; condition?: object | null } = object,
  >(
    entity: ResultType,
    getQuery: string,
    deleteQuery: string,
    deleteVariables: DeleteVariablesType = {} as DeleteVariablesType
  ) => {
    const entityId = (entity as BasicModel<ResultType>).id;
    const latestEntityVersion = await fetchLatestEntityVersion<ResultType>(entityId, getQuery);

    const result = await deleteAndHideEntity(
      { ...entity, _version: latestEntityVersion._version },
      deleteQuery,
      deleteVariables
    );

    return result;
  };

  const deleteAndHideEntity = async <
    ResultType extends object,
    DeleteVariablesType extends { input?: object; condition?: object | null } = object,
  >(
    entity: ResultType,
    deleteQuery: string,
    deleteVariables: DeleteVariablesType = {} as DeleteVariablesType
  ) => {
    const entityWithVersion: BasicModel<ResultType> = entity as BasicModel<ResultType>;
    const result = await mutation<ResultType>(deleteQuery, {
      input: { id: entityWithVersion.id, _version: entityWithVersion._version, ...deleteVariables.input },
      ...deleteVariables.condition,
    });
    return result;
  };

  const list = async <ResultType extends object>(
    listQuery: string,
    indexName?: string,
    indexValue?: string,
    additionalFilter?: LooseObject,
    lastSync?: DateTime,
    limit: number = 1000,
    getDeleted: boolean = false
  ) => {
    const method = getGraphQlMethodName(listQuery, 'query');
    const baseFilter = getFilters(indexName, indexValue, additionalFilter);
    const isSync = Boolean(listQuery.match(/query Sync[\W\w]*\([\W\w]*\$lastSync/gm));
    const filter = isSync && !getDeleted ? excludeDeletedFromFilter(baseFilter) : baseFilter;
    let entities: ResultType[] = [];
    let nextToken: string | null = '';

    try {
      while (!isNil(nextToken)) {
        const variables: { limit: number; filter: LooseObject | null; nextToken: string | null; lastSync?: number } = {
          limit,
          filter,
          nextToken: isEmpty(nextToken) ? null : nextToken,
        };
        if (isSync && lastSync) {
          variables.lastSync = lastSync.epochMilliseconds;
        }
        const listResultRaw = await client.request<{
          [method: string]: { items: (ResultType | null)[]; nextToken: string | null } | null;
        }>(listQuery, variables);
        const page = listResultRaw[method];

        const pageEntities = compact(page?.items ?? []).filter(
          (entity: { _deleted?: boolean }) => getDeleted || entity._deleted !== true
        );
        entities = [...entities, ...pageEntities];
        nextToken = page?.nextToken ?? null;
      }
      return entities;
    } catch (error) {
      // Never resolve to `[]` here: callers cannot tell a failed request from an empty
      // result set, and they would cache the emptiness as a successful fetch.
      throw toGraphQLApiError(error, method);
    }
  };

  const get = async <ResultType extends object>(getQuery: string, id: string) => {
    const method = getGraphQlMethodName(getQuery, 'query');
    const variables = { id };
    try {
      const data = await client.request<{ [method: string]: ResultType }>(getQuery, variables);

      return data[method] as BasicModel<ResultType>;
    } catch (error) {
      throw toGraphQLApiError(error, method);
    }
  };

  return {
    deleteAndHideEntity,
    deleteAndHideEntityWithFetchBefore,
    deleteEntityWithFetchBefore,
    get,
    list,
    mutation,
    mutationWithFetchBefore,
    query,
  };
};

export const {
  deleteAndHideEntity,
  deleteAndHideEntityWithFetchBefore,
  deleteEntityWithFetchBefore,
  get,
  list,
  mutation,
  mutationWithFetchBefore,
  query,
} = createGraphqlOperations(graphqlClient);
